import React, { useEffect, useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { useParams } from 'react-router-dom';
import { Row, Col, Divider, Form } from 'antd';

import { useGetBeraSummaryResult } from '@/hooks/useGetBeraSummaryResult';

import { BeraJobStepsRouteParams, useBeraJobStepsContext } from '../context';
import { useApplicationContext } from '@/context/v1/Application/context';
import { TasksAnalyzed } from './TasksAnalyzed';
import { Title } from '@/components/Typography';
import { Informations } from './Informations';
import { ResultsContainer } from './styles';
import { Comment } from './Comment';
import { Header } from './Header';
import { Tasks } from './Tasks';
import { ResultBera } from './ResultBera';
import { ResultENS } from './ResultENS';
import { ActionPlans } from './ActionPlans';
import { Footer } from './Footer';

const { useForm } = Form;

export function BeraJobResult(): JSX.Element {
	const [form] = useForm();
	const { organization, company } = useApplicationContext();
	const { bera_job_summary_id } = useParams<BeraJobStepsRouteParams>();
	const { beraJobSummaryId, handleTabsDisabledChange } = useBeraJobStepsContext();
	const [consolidated, setConsolidated] = useState<boolean>(false);

	const [isEditingComment, setIsEditingComment] = useState<boolean>(false);

	const { isLoading, data, isError } = useGetBeraSummaryResult({
		company_id: company?.id,
		organization_id: organization?.id,
		id: bera_job_summary_id ?? beraJobSummaryId
	});

	useEffect(() => {
		setConsolidated(data?.consolidated || false);
	}, [data?.consolidated]);

	const browserLanguage = window.navigator.language ?? 'en-US';

	useEffect(() => {
		if (data?.comment) {
			form.setFieldValue('comment', data?.comment);
		}

		handleTabsDisabledChange(true);

		return () => {
			handleTabsDisabledChange(false);
		};
	}, [data]);

	if (isError) {
		return (
			<Row justify="center">
				<Col span={24}>
					<span>{I18n.get('Oops... Something went wrong!')}</span>
				</Col>
			</Row>
		);
	}

	function handleCommentChange(editingComment: boolean): void {
		setIsEditingComment(editingComment);
	}

	function handleConsolidate(): void {
		setConsolidated(!consolidated);
	}

	return (
		<Row justify="center">
			<Col span={24} style={{ marginBottom: '2rem' }}>
				<Title level={4}>Result</Title>
			</Col>
			<Col xs={24}>
				<Form form={form}>
					<ResultsContainer>
						<Header />
						<Divider type="horizontal" />

						<Informations isLoading={isLoading} informations={data?.informations} />
						<Divider type="horizontal" />

						<TasksAnalyzed isLoading={isLoading} tasksAnalyzed={data?.analyzed_files} />

						<Tasks
							isLoading={isLoading}
							tasks={data?.tasks}
							bera_job_summary_id={bera_job_summary_id ?? beraJobSummaryId}
						/>

						<ResultBera
							isLoading={isLoading}
							cycleSummary={data?.cycle_summary}
							highestRPNs={data?.highest_rpn_scores}
						/>

						<ResultENS
							isLoading={isLoading}
							cycleSummary={data?.cycle_summary}
							ens={data?.informations?.gns || false}
							bera_job_summary_id={data?.id}
						/>

						<ActionPlans
							beraJobSummaryId={bera_job_summary_id ?? beraJobSummaryId}
							fileId={data?.informations?.file_id}
							workstationId={data?.informations?.workstation_id}
							rpn={data?.informations?.overall_score}
						/>
						<Comment
							loading={isLoading}
							consolidated={consolidated}
							onCommentChange={handleCommentChange}
						/>
						<Footer
							reportId={bera_job_summary_id ?? beraJobSummaryId}
							locale={browserLanguage}
							disabled={isEditingComment}
							consolidated={consolidated}
							onConsolidate={handleConsolidate}
						/>
					</ResultsContainer>
				</Form>
			</Col>
		</Row>
	);
}
